import React, { useRef, useState } from 'react'
import { useLocation } from 'react-router'
import { useServiceContext } from '../../root'
import SiteInfo from '../../components/staging/SiteInfo'
import { GridTableSwitch } from '../browser/GridTableSwitch'
import { RcmTableDataWrapper } from '../../components/rcm-table/RcmTableDataWrapper'
import Table from './Table'
import usePropertySearchService from '../../hooks/usePropertySearchService'
import useEntitySearchService from '../../hooks/useEntitySearchService'
import { useStagingContext } from '../../routes/staging'

// --- Stubs for missing components ---
// TableFilter and PropertySearchFilter real usage (replace stubs)
import TableFilter from './TableFilter'
import PropertySearchFilter from '../../propertySearch/property-search/PropertySearchFilter'
import PageMenu from './PageMenu'
const LayoutWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="layout-wrapper">{children}</div>
)
import RcmTableFilterTrigger from '../../components/rcm-table/RcmTableFilterTrigger'

// --- Mode mapping ---
const MODE = {
  missing: 'MissingDocuments',
  user: 'MyDocuments',
  study: 'StudyDocuments',
  propertySearch: 'property-search'
}

function getModeFromLocation(location: any): keyof typeof MODE {
  // Example: /staging/docs/:mode
  // You may want to adjust this logic to match your router config
  const path = location.pathname
  if (path.includes('MissingDocuments')) return 'missing'
  if (path.includes('MyDocuments')) return 'user'
  if (path.includes('StudyDocuments')) return 'study'
  if (path.includes('property-search')) return 'propertySearch'
  return 'study' // default
}

interface PageStagingDocsProps {
  contextId?: string
}

const PageStagingDocs: React.FC<PageStagingDocsProps> = () => {
  const [totalElementsTracker, setTotalElementTracker] = useState<any>(null)
  const ref = useRef<HTMLDivElement>(null)
  const context = useServiceContext()
  const { service, api, store } = context
  const { translate } = service.filter
  const level: string = service.staging.getLevel?.() ?? ''
  const { contextId } = useStagingContext()

  const location = useLocation()
  const mode = getModeFromLocation(location)

  // --- Property/entity search services ---
  const { loadData: propertySearchLoadData } = usePropertySearchService(true, contextId?.toString())
  const { getLinkedEntities, properties: propertiesFromService } = useEntitySearchService()

  // --- User role logic ---
  // These should match legacy logic for isStudyUser/isSiteUser
  const isStudyUser = service.auth?.getRole?.() === 'ETMF_STUDY_USER'
  const isSiteUser = service.auth?.getRole?.() === 'ETMF_SITE_USER'

  // --- Completion/type logic ---
  const getCompletionType = () => {
    return mode === 'study'
      ? 'COMPLETED'
      : mode === 'user'
        ? 'UPLOADED'
        : mode === 'missing'
          ? 'NOT_STARTED'
          : 'UPLOADED'
  }
  const getType = () => {
    switch (mode) {
      case 'user':
        return getCompletionType() === 'UPLOADED' ? 'DOCUMENTS_UPLOADED' : 'DOCUMENTS_FILED'
      case 'missing':
        return 'DOCUMENTS_MISSING'
      case 'study':
        return 'DOCUMENTS_FOR_LEVEL'
      default:
        return undefined
    }
  }

  // --- Default filter ---
  const defaultFilter = {
    filter: {
      zone: '',
      section: '',
      milestone: '',
      completionType: getCompletionType(),
      name: null,
      state: 'COMPLETED',
      artifacts: null,
      level: 'ALL',
      fsiIds: null,
      countriesfsiIds: null,
      siteFsiId: service.staging.isLevel?.(service.staging.LEVEL?.site) ? contextId : null,
      envFsiId: service.staging.isLevel?.(service.staging.LEVEL?.environment) ? contextId : null,
      placeholderName: '',
      country: [],
      site: [],
      type: getType()
    }
  }

  // --- Data loading logic ---
  // Property search mode: use propertySearchLoadData and track total elements
  const loadDataForPropertySearch = async (params: any, filter: any) => {
    const promise = propertySearchLoadData(params, filter)
    promise.then(async (data) => {
      if (data) {
        await getLinkedEntities(data)
      }
    })
    setTotalElementTracker(promise)
    const data = await promise
    return {
      elements: data.elements,
      hasNextPage: data.hasNextPage,
      totalElements: data.totalElements
    }
  }

  // Standard docs mode: build request from filter form, call correct API, and track total elements
  const loadData = async (params: any, _filter?: any) => {
    const request: any = {}
    const filterForm = store?.formRefStaging?.values?.filter || {}
    if (filterForm.zone) request.zone = filterForm.zone
    if (filterForm.section) request.section = filterForm.section
    if (filterForm.milestone) request.milestone = filterForm.milestone
    if (filterForm.completionType) request.completionType = filterForm.completionType
    if (filterForm.placeholderName) request.name = filterForm.placeholderName
    if (filterForm.state) request.state = filterForm.state
    if (filterForm.artifacts) request.artifacts = filterForm.artifacts
    request.level = filterForm.level
    if (filterForm.level === 'COUNTRY') {
      request.fsiIds = filterForm.country
    } else if (filterForm.level === 'SITE') {
      request.fsiIds = filterForm.site
      request.countriesfsiIds = filterForm.country
    }
    let promise
    const contextIdData = contextId ? contextId.toString() : ''
    if (service.staging.isLevel?.(service.staging.LEVEL?.site)) {
      if (mode === 'user') {
        request.type = request.completionType === 'UPLOADED' ? 'DOCUMENTS_UPLOADED' : 'DOCUMENTS_FILED'
        request.siteFsiId = contextId
        promise = api.etmf.getSiteUserDocs(contextIdData, params, request)
      } else if (mode === 'missing') {
        request.type = 'DOCUMENTS_MISSING'
        request.siteFsiId = contextId
        promise = api.etmf.getSiteUserDocs(contextIdData, params, request)
      }
    } else {
      if (mode === 'study') {
        request.type = 'DOCUMENTS_FOR_LEVEL'
        request.envFsiId = contextId
        promise = api.etmf.getStudyUserDocs(contextIdData, params, request)
      } else if (mode === 'user') {
        request.type = request.completionType === 'UPLOADED' ? 'DOCUMENTS_UPLOADED' : 'DOCUMENTS_FILED'
        request.envFsiId = contextId
        promise = api.etmf.getStudyUserDocs(contextIdData, params, request)
      } else if (mode === 'missing') {
        request.type = 'DOCUMENTS_MISSING'
        request.envFsiId = contextId
        promise = api.etmf.getStudyUserDocs(contextIdData, params, request)
      }
    }
    if (promise) {
      promise.then(async (data: any) => {
        if (data && data.elements) {
          await getLinkedEntities(data)
        }
      })
      setTotalElementTracker(promise)
      const data = await promise
      return {
        elements: data?.elements || [],
        totalElements: data?.totalElements || 0,
        hasNextPage: data?.hasNextPage
      }
    }
    // fallback if no promise (should not happen)
    setTotalElementTracker(null)
    return { elements: [], totalElements: 0, hasNextPage: false }
  }

  const getPageTitle = (mode: keyof typeof MODE) => {
    switch (mode) {
      case 'missing':
        return 'STAGING_AREA_SECTION_MISSING_DOCS'
      case 'user':
        return 'STAGING_AREA_SECTION_USER_DOCS'
      case 'study':
        return 'STAGING_AREA_SECTION_STUDY_DOCS'
      case 'propertySearch':
        return 'STAGING_AREA_SECTION_PROPERTY_SEARCH'
      default:
        return ''
    }
  }

  return (
    <div className="mcc-content-frame theme-sandman">
      <RcmTableDataWrapper
        doPreload={true}
        loadData={mode === 'propertySearch' ? loadDataForPropertySearch : loadData}
        defaultSortBy={mode === 'propertySearch' ? 'name' : 'zone'}
        defaultSortDesc={false}
        persistenceKey={'persistenceKey'}
      >
        <div className="d-flex flex-wrap">
          <h1>{translate(getPageTitle(mode))}</h1>
          <div className="align-right d-flex">
            {mode !== 'propertySearch' && <PageMenu />}
            <RcmTableFilterTrigger />
            <GridTableSwitch />
          </div>
        </div>
        <div className="clearfix">
          <div className="pull-left">
            <div className="mb20 mt20">
              <SiteInfo contextId={contextId ? contextId.toString() : ''} level={level} />
            </div>
          </div>
        </div>
        <div>
          {mode === 'propertySearch' && (
            <PropertySearchFilter
              persistenceKey={'studiesTableFilter'}
              properties={propertiesFromService ? Object.values(propertiesFromService) : []}
              defaultFilter={defaultFilter}
            />
          )}
          {mode !== 'propertySearch' && (
            <TableFilter
              config={{ contextId, mode, isStudyUser }}
              properties={propertiesFromService}
              defaultFilter={defaultFilter}
            />
          )}
          <Table
            totalElements={totalElementsTracker}
            isStudyUser={isStudyUser}
            isSiteUser={isSiteUser}
            contextId={contextId ? contextId.toString() : ''}
            mode={mode}
          />
        </div>
      </RcmTableDataWrapper>
    </div>
  )
}

export default PageStagingDocs
